/**
 * Unified timeclock client.
 *
 * Every surface that lets someone clock in / out / take a break (FAB, booking
 * job-timer prompt, day-view prompt, PIN kiosk, logout prompt, T-Sheets admin
 * entry, time-request approval) calls these helpers, which in turn call the
 * `timeclock_*` RPCs. Location capture, background-tracking toggles and the
 * cross-component "something changed" event live here so no surface drifts.
 */
import { supabase } from './supabase';

export type TimeclockSource =
  | 'fab'
  | 'booking'
  | 'day_view'
  | 'kiosk'
  | 'logout'
  | 'admin'
  | 'request'
  | 'app';

export interface TimeclockEntry {
  id: string;
  business_id: string;
  resource_id: string;
  clock_in: string;
  clock_out: string | null;
  break_start: string | null;
  break_end: string | null;
  total_minutes: number | null;
  total_hours: number | null;
  total_earnings: number | null;
  rate_amount: number | null;
  compensation_type: string | null;
  status: string | null;
  process_status: string | null;
  notes: string | null;
  clock_in_lat: number | null;
  clock_in_lng: number | null;
  clock_out_lat: number | null;
  clock_out_lng: number | null;
  distance_km: number | null;
  clock_source: string | null;
  regular_minutes?: number | null;
  overtime_minutes?: number | null;
  double_time_minutes?: number | null;
  overtime_earnings?: number | null;
  needs_review?: boolean;
  review_reason?: string | null;
}

export interface TimeclockResource {
  id: string;
  first_name: string | null;
  last_name: string | null;
  compensation_type: string;
  hourly_rate: number | null;
  is_admin: boolean;
  is_self: boolean;
}

export interface ActiveJobTimer {
  id: string;
  booking_id: string;
  started_at: string;
}

export interface TimeclockStatus {
  resource: TimeclockResource;
  entry: TimeclockEntry | null;
  active_job_timers: ActiveJobTimer[];
  server_time: string;
}

export interface TimeclockResult {
  entry: TimeclockEntry;
  resource_id: string;
  is_self: boolean;
  already_open?: boolean;
  stopped_job_timers?: number;
}

export interface ClockLocation {
  lat: number;
  lng: number;
}

/** Fired on window after any successful clock action so open widgets refresh. */
export const TIMECLOCK_CHANGED_EVENT = 'timeclock:changed';

export interface TimeclockChangedDetail {
  action: 'clock_in' | 'clock_out' | 'break_start' | 'break_end' | 'record';
  resourceId: string;
  entry: TimeclockEntry;
}

function emitChanged(detail: TimeclockChangedDetail) {
  try {
    window.dispatchEvent(new CustomEvent<TimeclockChangedDetail>(TIMECLOCK_CHANGED_EVENT, { detail }));
  } catch {
    /* non-browser context */
  }
}

export function onTimeclockChanged(handler: (detail: TimeclockChangedDetail) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<TimeclockChangedDetail>).detail);
  window.addEventListener(TIMECLOCK_CHANGED_EVENT, listener);
  return () => window.removeEventListener(TIMECLOCK_CHANGED_EVENT, listener);
}

/** Machine-readable reason attached by the RPCs (Postgres DETAIL). */
export type TimeclockErrorCode =
  | 'not_authorized'
  | 'kiosk_disabled'
  | 'invalid_passcode'
  | 'no_resource'
  | 'resource_not_found'
  | 'backdate_not_allowed'
  | 'not_clocked_in'
  | 'clock_out_before_clock_in'
  | 'break_already_running'
  | 'break_already_taken'
  | 'no_break_running'
  | 'break_incomplete'
  | 'break_outside_shift'
  | 'entry_paid'
  | 'entry_not_found'
  | 'not_timeclock'
  | 'clock_out_required'
  | 'unknown';

export class TimeclockError extends Error {
  code: TimeclockErrorCode;
  constructor(message: string, code: TimeclockErrorCode) {
    super(message);
    this.name = 'TimeclockError';
    this.code = code;
  }
}

function toTimeclockError(error: unknown): TimeclockError {
  if (error instanceof TimeclockError) return error;
  const e = (error ?? {}) as { message?: string; details?: string; code?: string };
  const detail = (e.details || '').trim() as TimeclockErrorCode;
  const known: TimeclockErrorCode[] = [
    'not_authorized', 'kiosk_disabled', 'invalid_passcode', 'no_resource', 'resource_not_found',
    'backdate_not_allowed', 'not_clocked_in', 'clock_out_before_clock_in', 'break_already_running',
    'break_already_taken', 'no_break_running', 'break_incomplete', 'break_outside_shift',
    'entry_paid', 'entry_not_found', 'not_timeclock', 'clock_out_required',
  ];
  const code = known.includes(detail) ? detail : 'unknown';
  const message = e.message && !/^[a-z_]+$/.test(e.message)
    ? e.message
    : 'Something went wrong with the time clock. Please try again.';
  return new TimeclockError(message, code);
}

function requireClient() {
  if (!supabase) throw new TimeclockError('Not connected. Please refresh and try again.', 'unknown');
  return supabase;
}

// ---------------------------------------------------------------------------
// Location
// ---------------------------------------------------------------------------

/**
 * Best-effort position for a clock event. Uses the native background-location
 * plugin when available; in a browser it only reads the position when the site
 * already has permission, so clocking never triggers a permission pop-up.
 */
export async function captureClockLocation(): Promise<ClockLocation | null> {
  try {
    const { getPosition } = await import('../services/backgroundLocationService');
    const pos = await getPosition(false);
    if (pos) return pos;
  } catch {
    /* plugin unavailable */
  }

  if (typeof navigator === 'undefined' || !navigator.geolocation) return null;
  try {
    if (navigator.permissions?.query) {
      const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      if (status.state !== 'granted') return null;
    } else {
      return null;
    }
  } catch {
    return null;
  }

  return new Promise<ClockLocation | null>((resolve) => {
    const timer = setTimeout(() => resolve(null), 5000);
    navigator.geolocation.getCurrentPosition(
      (p) => { clearTimeout(timer); resolve({ lat: p.coords.latitude, lng: p.coords.longitude }); },
      () => { clearTimeout(timer); resolve(null); },
      { enableHighAccuracy: false, timeout: 4500, maximumAge: 60_000 },
    );
  });
}

async function readOdometerKm(): Promise<number | null> {
  try {
    const { getOdometer } = await import('../services/backgroundLocationService');
    return await getOdometer();
  } catch {
    return null;
  }
}

/** Background tracking follows the signed-in user's own shift only. */
async function syncBackgroundTracking(isSelf: boolean, clockedIn: boolean) {
  if (!isSelf) return;
  try {
    const { setClockedIn } = await import('../services/backgroundLocationService');
    await setClockedIn(clockedIn);
  } catch {
    /* native only */
  }
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export interface TimeclockTarget {
  /** Omit to act on the signed-in user's own resource. */
  resourceId?: string | null;
  /** Kiosk flow: identifies the resource by their clock-in PIN. */
  passcode?: string | null;
}

export async function getTimeclockStatus(businessId: string, target: TimeclockTarget = {}): Promise<TimeclockStatus> {
  const client = requireClient();
  const { data, error } = await client.rpc('timeclock_status', {
    p_business_id: businessId,
    p_resource_id: target.resourceId ?? null,
    p_passcode: target.passcode ?? null,
  });
  if (error) throw toTimeclockError(error);
  return data as TimeclockStatus;
}

/**
 * Like getTimeclockStatus but returns null instead of throwing when the signed-in
 * user has no resource row (business owners without a team-member record).
 */
export async function getOwnTimeclockStatus(businessId: string): Promise<TimeclockStatus | null> {
  try {
    return await getTimeclockStatus(businessId);
  } catch (e) {
    if (e instanceof TimeclockError && (e.code === 'no_resource' || e.code === 'not_authorized')) return null;
    throw e;
  }
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export interface ClockInOptions extends TimeclockTarget {
  source: TimeclockSource;
  notes?: string | null;
  /** Admin-only backdated start. */
  clockIn?: Date | string | null;
  /** Skip GPS capture (kiosk tablets, admin tools). */
  skipLocation?: boolean;
}

export async function clockIn(businessId: string, options: ClockInOptions): Promise<TimeclockResult> {
  const client = requireClient();
  const location = options.skipLocation ? null : await captureClockLocation();
  const { data, error } = await client.rpc('timeclock_clock_in', {
    p_business_id: businessId,
    p_resource_id: options.resourceId ?? null,
    p_source: options.source,
    p_lat: location?.lat ?? null,
    p_lng: location?.lng ?? null,
    p_notes: options.notes ?? null,
    p_clock_in: options.clockIn ? new Date(options.clockIn).toISOString() : null,
    p_passcode: options.passcode ?? null,
  });
  if (error) throw toTimeclockError(error);
  const result = data as TimeclockResult;
  await syncBackgroundTracking(result.is_self, true);
  emitChanged({ action: 'clock_in', resourceId: result.resource_id, entry: result.entry });
  return result;
}

export interface ClockOutOptions extends TimeclockTarget {
  source: TimeclockSource;
  /** Admin-only explicit end time. */
  clockOut?: Date | string | null;
  /** Running job timers are stopped with the shift unless set to false. */
  stopJobTimers?: boolean;
  skipLocation?: boolean;
}

export async function clockOut(businessId: string, options: ClockOutOptions): Promise<TimeclockResult> {
  const client = requireClient();
  const [location, distanceKm] = options.skipLocation
    ? [null, null]
    : await Promise.all([captureClockLocation(), readOdometerKm()]);
  const { data, error } = await client.rpc('timeclock_clock_out', {
    p_business_id: businessId,
    p_resource_id: options.resourceId ?? null,
    p_source: options.source,
    p_lat: location?.lat ?? null,
    p_lng: location?.lng ?? null,
    p_distance_km: distanceKm,
    p_clock_out: options.clockOut ? new Date(options.clockOut).toISOString() : null,
    p_stop_job_timers: options.stopJobTimers ?? true,
    p_passcode: options.passcode ?? null,
  });
  if (error) throw toTimeclockError(error);
  const result = data as TimeclockResult;
  await syncBackgroundTracking(result.is_self, false);
  emitChanged({ action: 'clock_out', resourceId: result.resource_id, entry: result.entry });
  return result;
}

async function breakAction(businessId: string, action: 'start' | 'end', target: TimeclockTarget): Promise<TimeclockResult> {
  const client = requireClient();
  const { data, error } = await client.rpc('timeclock_break', {
    p_business_id: businessId,
    p_action: action,
    p_resource_id: target.resourceId ?? null,
    p_passcode: target.passcode ?? null,
  });
  if (error) throw toTimeclockError(error);
  const result = data as TimeclockResult;
  emitChanged({ action: action === 'start' ? 'break_start' : 'break_end', resourceId: result.resource_id, entry: result.entry });
  return result;
}

export const startBreak = (businessId: string, target: TimeclockTarget = {}) => breakAction(businessId, 'start', target);
export const endBreak = (businessId: string, target: TimeclockTarget = {}) => breakAction(businessId, 'end', target);

export interface RecordEntryInput {
  clockIn: Date | string;
  clockOut?: Date | string | null;
  breakStart?: Date | string | null;
  breakEnd?: Date | string | null;
  notes?: string | null;
  source?: Extract<TimeclockSource, 'admin' | 'request'>;
}

const iso = (v: Date | string | null | undefined) => (v ? new Date(v).toISOString() : null);

/** Admin: record a shift by hand (T-Sheets "Add entry", approved time requests). */
export async function recordTimeclockEntry(businessId: string, resourceId: string, input: RecordEntryInput): Promise<TimeclockResult> {
  const client = requireClient();
  const { data, error } = await client.rpc('timeclock_record_entry', {
    p_business_id: businessId,
    p_resource_id: resourceId,
    p_clock_in: iso(input.clockIn),
    p_clock_out: iso(input.clockOut),
    p_break_start: iso(input.breakStart),
    p_break_end: iso(input.breakEnd),
    p_notes: input.notes ?? null,
    p_source: input.source ?? 'admin',
  });
  if (error) throw toTimeclockError(error);
  const result = data as TimeclockResult;
  emitChanged({ action: 'record', resourceId: result.resource_id, entry: result.entry });
  return result;
}

// ---------------------------------------------------------------------------
// Admin corrections
// ---------------------------------------------------------------------------

export interface UpdateEntryInput {
  clockIn: Date | string;
  clockOut?: Date | string | null;
  breakStart?: Date | string | null;
  breakEnd?: Date | string | null;
  notes?: string | null;
  /** Required to change a shift that has already been paid; records an adjustment for the delta. */
  allowPaid?: boolean;
  reason?: string | null;
  /** Admin override of the pay rate snapshot for this shift. */
  rateAmount?: number | null;
}

export interface UpdateEntryResult {
  entry: TimeclockEntry;
  was_paid: boolean;
  adjustment: { id: string; delta: number; notes: string | null } | null;
}

/**
 * Edit a shift through the engine: minutes, rate snapshot and overtime are all
 * recomputed server-side. Throws TimeclockError('entry_paid') when the shift
 * was already paid and allowPaid is not set.
 */
export async function updateTimeclockEntry(entryId: string, input: UpdateEntryInput): Promise<UpdateEntryResult> {
  const client = requireClient();
  const { data, error } = await client.rpc('timeclock_update_entry', {
    p_entry_id: entryId,
    p_clock_in: iso(input.clockIn),
    p_clock_out: iso(input.clockOut),
    p_break_start: iso(input.breakStart),
    p_break_end: iso(input.breakEnd),
    p_notes: input.notes ?? null,
    p_allow_paid: input.allowPaid ?? false,
    p_reason: input.reason ?? null,
    p_rate_amount: input.rateAmount ?? null,
  });
  if (error) throw toTimeclockError(error);
  const result = data as UpdateEntryResult;
  emitChanged({ action: 'record', resourceId: result.entry.resource_id, entry: result.entry });
  return result;
}

/** Clear the needs-review flag on a shift an admin has looked at. */
export async function confirmTimeclockEntry(entryId: string): Promise<TimeclockEntry> {
  const client = requireClient();
  const { data, error } = await client.rpc('timeclock_confirm_entry', { p_entry_id: entryId });
  if (error) throw toTimeclockError(error);
  return data as TimeclockEntry;
}

export interface PayrollResourceTotals {
  resource_id: string;
  timeclock_minutes: number;
  regular_minutes: number;
  overtime_minutes: number;
  double_time_minutes: number;
  timeclock_earnings: number;
  overtime_earnings: number;
  flat_rate_hours: number;
  flat_rate_earnings: number;
  commission_earnings: number;
  bonus_earnings: number;
  tip_earnings: number;
  adjustment_earnings: number;
  total_earnings: number;
  unpaid_earnings: number;
  entry_count: number;
  needs_review_count: number;
  open_shift_count: number;
}

/** Per-resource pay-period totals summed by the database (no client math). */
export async function getPayrollResourceTotals(
  businessId: string,
  utcStart: string,
  utcEnd: string,
): Promise<Record<string, PayrollResourceTotals>> {
  const client = requireClient();
  const { data, error } = await client.rpc('payroll_resource_totals', {
    p_business_id: businessId,
    p_start: utcStart,
    p_end: utcEnd,
  });
  if (error) throw toTimeclockError(error);
  const out: Record<string, PayrollResourceTotals> = {};
  for (const raw of (data ?? []) as Record<string, unknown>[]) {
    const row = Object.fromEntries(
      Object.entries(raw).map(([k, v]) => [k, k === 'resource_id' ? v : Number(v ?? 0)]),
    ) as unknown as PayrollResourceTotals;
    out[row.resource_id] = row;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Presentation helpers shared by every surface
// ---------------------------------------------------------------------------

export function isOnBreak(entry: TimeclockEntry | null | undefined): boolean {
  return !!entry && !!entry.break_start && !entry.break_end;
}

/** Worked milliseconds so far for an open shift (break time excluded). */
export function workedMs(entry: TimeclockEntry, now: Date = new Date()): number {
  const end = entry.clock_out ? new Date(entry.clock_out) : now;
  let ms = end.getTime() - new Date(entry.clock_in).getTime();
  if (entry.break_start) {
    const bEnd = entry.break_end ? new Date(entry.break_end) : end;
    ms -= Math.max(0, bEnd.getTime() - new Date(entry.break_start).getTime());
  }
  return Math.max(0, ms);
}

/** Milliseconds of the currently running break, 0 when not on break. */
export function currentBreakMs(entry: TimeclockEntry, now: Date = new Date()): number {
  if (!isOnBreak(entry)) return 0;
  return Math.max(0, now.getTime() - new Date(entry.break_start as string).getTime());
}

export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
